import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import styles from "./PlaberCard.module.css";
import "../../styles/global.css";
import { supabase } from "../../supabase"; 

async function getImageUrl(bucketName, filePath) {
  const { data, error } = supabase
    .storage
    .from(bucketName)
    .getPublicUrl(filePath);
  if (error) {
    console.error("Error fetching image URL:", error);
    return null;
  }
  console.log("Fetched URL:", data.publicUrl);
  return data.publicUrl;
}

// Easing 함수 (점점 느려지는 효과)
function easeOutQuad(t) {
  return t * (2 - t);
}

function PlaberCard({ animationType = "A", keyTrigger }) {
    const photoURL = "https://hips.hearstapps.com/hmg-prod/images/lionel-messi-celebrates-after-their-sides-third-goal-by-news-photo-1686170172.jpg";
    const fallbackImageURL = "https://placehold.co/600x400?text=No+Image";
    
    const [score, setScore] = useState(0);
    const [isCoverVisible, setIsCoverVisible] = useState(true); // 커버 상태
    const targetScore = 87; // 최종 점수
  
    const [logoLoaded, setLogoLoaded] = useState(false);
    const logoURL = "https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/plab24_logo.svg";
  
    const [iconUrls, setIconUrls] = useState([]);

    useEffect(() => {
      const fetchIcons = async () => {
        const icons = await Promise.all([
          getImageUrl("asset", "badge_plab24_shoot.svg"),
          getImageUrl("asset", "badge_plab24_manner.svg"),
          getImageUrl("asset", "badge_plab24_ptl.svg"),
        ]);
  
        setIconUrls(icons);
      };
  
      fetchIcons();
    }, []);

    // 커버가 0.5초 후 자동으로 사라지도록 설정
    useEffect(() => {
      setIsCoverVisible(true); // 리셋 시 커버를 다시 보여줌
      const coverTimeout = setTimeout(() => {
        setIsCoverVisible(false); // 0.5초 후 커버 제거
      }, 1000);
  
      return () => clearTimeout(coverTimeout); // 컴포넌트 언마운트 시 타이머 정리
    }, [keyTrigger]);
  
    // 숫자 카운트 애니메이션
    useEffect(() => {
      if (isCoverVisible) return; // 커버가 제거되지 않으면 애니메이션 시작 안 함
  
      let start = 0;
      const duration = 2000; // 전체 애니메이션 지속 시간 (ms)
      const startTime = performance.now(); // 시작 시간
  
      function updateScore(currentTime) {
        const elapsedTime = currentTime - startTime; // 경과 시간
        const progress = Math.min(elapsedTime / duration, 1); // 진행 비율 (0 ~ 1)
        const easedProgress = easeOutQuad(progress); // easing 적용
  
        // 점수를 업데이트
        const newScore = Math.floor(targetScore * easedProgress);
        setScore(newScore);
  
        // 애니메이션 종료 조건
        if (progress < 1) {
          requestAnimationFrame(updateScore); // 다음 프레임 호출
        }
      }
  
      requestAnimationFrame(updateScore); // 애니메이션 시작
    }, [keyTrigger, isCoverVisible]);

  const cardVariants = {
    A: {
      initial: { rotateY: 0 },
      animate: { rotateY: 3600 },
      transition: { duration: 5, ease: [0.1, 0.1, 0.1, 1] },
    },
    B: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.5, ease: "easeInOut" },
    },
    C: {
      initial: { scale: 0.5 },
      animate: { scale: 1 },
      transition: { duration: 0.8, type: "spring", stiffness: 120 },
    },
    D: {
      initial: { 
        opacity: 0, 
        scale: 0.5, 
        filter: 'brightness(50%)'
      },
      animate: { 
        opacity: 1, 
        scale: [1.5, 0.5, 1.1, 0.9, 1], 
        filter: ['brightness(150%)', 'brightness(100%)'],
        boxShadow: "0px 0px 30px rgba(255, 255, 255, 0.8)"
      },
      transition: { 
        duration: 1, 
        ease: "easeInOut",
        scale: {
          duration: 0.6,
          times: [0, 0.5, 1],
          ease: "easeInOut"
        },
        filter: {
          duration: 0.3,
          times: [0, 0.5, 1],
          ease: "easeInOut",
          repeat: 1,
          repeatType: "reverse"
        }
      }
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      className={styles.card}
      key={keyTrigger}
      initial={cardVariants[animationType]?.initial}
      animate={cardVariants[animationType]?.animate}
      transition={cardVariants[animationType]?.transition}
      whileHover={animationType === 'D' ? {
        scale: 1.05,
        boxShadow: "0px 0px 30px rgba(255, 255, 255, 0.8)",
        transition: { duration: 0.3 }
      } : {}}
    >
      {/* 커버 */}
      {isCoverVisible && (
        <div className={styles.cover}>
          <img 
            src={logoURL} 
            alt="Plab24 Logo" 
            className={styles.coverLogo}
            onLoad={() => setLogoLoaded(true)}
            onError={(e) => {
              console.error('Logo 로딩 실패:', e);
              e.target.style.display = 'none';
            }}
            style={{
              opacity: logoLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          />
        </div>
      )}

      {/* 상단 영역 */}
      <motion.div
        className={styles.photoSection}
        style={{ backgroundImage: `url(${photoURL})` }}
      >
        <div className={styles.scoreOver}>
          <motion.div
            className={styles.scoreGroup}>
            <motion.div
              className={styles.score}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
            >
              {score}
            </motion.div>
            <motion.div
              className={styles.region}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
            >
              SEOUL
            </motion.div>
          </motion.div>
          <motion.div
            className={styles.badgeGroup}>
            {iconUrls.map((iconUrl, index) => (
              <img key={index} src={iconUrl} alt={`Card Icon ${index + 1}`} />
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* 하단 영역 */}
      <motion.div
        className={styles.statsSection}
        variants={animationType === "B" ? containerVariants : null}
        initial="hidden"
        animate="visible"
      >
        {[
          { name: "레벨", value: "84" },
          { name: "공격", value: "72" },
          { name: "수비", value: "96" },
          { name: "매너", value: "89" },
          { name: "매치", value: "52" },
          { name: "POM", value: "12" },
          { name: "보낸 칭찬", value: "428" },
          { name: "받은 칭찬", value: "123" },
        ].map((stat, index) => (
          <motion.div
            key={index}
            className={styles.stat}
            variants={animationType === "B" ? itemVariants : null}
          >
            <span className={styles.statName}>{stat.name}</span>
            <span className={styles.statValue}>{stat.value}</span>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}

export default PlaberCard;